'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useApp } from '@/context/AppContext'
import { useLang } from '@/context/LangContext'
import { api } from '@/lib/api'
import { Store, ChevronRight, Delete } from 'lucide-react'
import { LangSwitch } from '@/components/LangSwitch'

type Step = 'select_shop' | 'select_user' | 'enter_pin'

export default function LoginPage() {
  const { setSession } = useApp()
  const { t } = useLang()
  const router = useRouter()

  const [step, setStep] = useState<Step>('select_shop')
  const [shops, setShops] = useState<any[]>([])
  const [users, setUsers] = useState<any[]>([])
  const [selectedShop, setSelectedShop] = useState<any>(null)
  const [selectedUser, setSelectedUser] = useState<any>(null)
  const [pin, setPin] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [dbError, setDbError] = useState(false)

  useEffect(() => {
    api.getShops()
      .then(data => { setShops(data); setLoading(false) })
      .catch(() => { setLoading(false); setDbError(true) })
  }, [])

  async function selectShop(shop: any) {
    setSelectedShop(shop)
    const users = await api.getUsers(shop.id)
    setUsers(users)
    setStep('select_user')
  }

  function selectUser(user: any) {
    setSelectedUser(user)
    setPin('')
    setError('')
    setStep('enter_pin')
  }

  async function submitPin(pinValue: string) {
    if (!selectedUser) return
    try {
      const { user, shop } = await api.login(selectedUser.id, pinValue)
      setSession({ shop, user })
      router.replace('/dashboard')
    } catch {
      setError('PIN incorrect ❌')
      setPin('')
    }
  }

  function addDigit(d: string) {
    if (pin.length >= 4) return
    const newPin = pin + d
    setPin(newPin)
    setError('')
    if (newPin.length === 4) setTimeout(() => submitPin(newPin), 100)
  }

  function deleteDigit() {
    setPin(p => p.slice(0, -1))
    setError('')
  }

  const roleLabel: Record<string, string> = {
    patron: '👑 Patron', vendeur: '🛒 Vendeur', caissier: '💰 Caissier',
  }

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-indigo-600">
      <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
    </div>
  )

  if (dbError) return (
    <div className="min-h-screen flex items-center justify-center bg-indigo-600 p-6">
      <div className="bg-white rounded-3xl p-6 w-full max-w-sm text-center shadow-2xl">
        <div className="text-5xl mb-3">⚠️</div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Connexion base de données impossible</h2>
        <p className="text-gray-500 text-sm mb-5">Vérifie que WAMP est lancé et que MySQL est démarré (icône verte).</p>
        <button onClick={() => { setDbError(false); setLoading(true); api.getShops().then(d => { setShops(d); setLoading(false) }).catch(() => { setLoading(false); setDbError(true) }) }}
          className="w-full py-3 bg-indigo-600 text-white rounded-2xl font-bold active:scale-95">
          Réessayer
        </button>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 to-indigo-800 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-white/20 rounded-3xl flex items-center justify-center mx-auto mb-3">
            <Store className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">Stoke</h1>
          <p className="text-indigo-200 mt-1">Gestion de votre boutique</p>
        </div>

        <div className="bg-white rounded-3xl p-6 shadow-2xl">

          {step === 'select_shop' && (
            <>
              <h2 className="text-xl font-bold text-gray-800 mb-4">Choisir la boutique</h2>
              {shops.length === 0 ? (
                <div className="text-center py-8">
                  <Store className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-400 mb-4">Aucune boutique</p>
                  <button onClick={() => router.push('/setup')} className="w-full py-3 bg-indigo-600 text-white rounded-2xl font-bold">
                    Créer une boutique
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {shops.map(shop => (
                    <button key={shop.id} onClick={() => selectShop(shop)}
                      className="flex items-center justify-between p-4 rounded-2xl border-2 border-gray-100 hover:border-indigo-400 hover:bg-indigo-50 transition-all active:scale-95">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                          <Store className="w-5 h-5 text-indigo-600" />
                        </div>
                        <span className="font-semibold text-gray-800">{shop.name}</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {step === 'select_user' && (
            <>
              <button onClick={() => setStep('select_shop')} className="text-indigo-600 font-medium mb-4">← Retour</button>
              <h2 className="text-xl font-bold text-gray-800 mb-1">Qui êtes-vous?</h2>
              <p className="text-gray-500 text-sm mb-4">{selectedShop?.name}</p>
              <div className="flex flex-col gap-3">
                {users.map(user => (
                  <button key={user.id} onClick={() => selectUser(user)}
                    className="flex items-center justify-between p-4 rounded-2xl border-2 border-gray-100 hover:border-indigo-400 hover:bg-indigo-50 transition-all active:scale-95">
                    <div>
                      <p className="font-semibold text-gray-800 text-left">{user.name}</p>
                      <p className="text-sm text-gray-500">{roleLabel[user.role]}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </button>
                ))}
              </div>
            </>
          )}

          {step === 'enter_pin' && (
            <>
              <button onClick={() => setStep('select_user')} className="text-indigo-600 font-medium mb-4">← Retour</button>
              <h2 className="text-xl font-bold text-gray-800 mb-1">Entrer le PIN</h2>
              <p className="text-gray-500 text-sm mb-6">{selectedUser?.name}</p>
              <div className="flex justify-center gap-4 mb-4">
                {[0,1,2,3].map(i => (
                  <div key={i} className={`w-4 h-4 rounded-full transition-all ${i < pin.length ? 'bg-indigo-600 scale-110' : 'bg-gray-200'}`} />
                ))}
              </div>
              {error && <p className="text-center text-red-500 font-medium mb-3">{error}</p>}
              <div className="grid grid-cols-3 gap-3">
                {['1','2','3','4','5','6','7','8','9','','0','⌫'].map((d, i) => (
                  <button key={i}
                    onClick={() => d === '⌫' ? deleteDigit() : d ? addDigit(d) : null}
                    className={`h-14 rounded-2xl text-xl font-bold transition-all active:scale-90 ${
                      !d ? '' : d === '⌫'
                        ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        : 'bg-gray-50 text-gray-800 hover:bg-indigo-50 hover:text-indigo-700 border border-gray-200'
                    }`}
                    disabled={!d}>
                    {d === '⌫' ? <Delete className="w-5 h-5 mx-auto" /> : d}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
